import type { ModelInfo } from '@paradigm/terminal-core';

import { defaultModelLabel } from './model-labels';

/**
 * Native offline fallback for the model picker, shown before (or when) the live
 * `/api/models` fetch resolves. Keyed by gateway host suffix because different
 * clusters may offer different catalogs.
 *
 * Ids follow the `provider/company/model/version` convention. The live catalog
 * replaces this list once it loads, so the table only needs sensible defaults.
 */
interface StaticModel {
  readonly id: string;
  readonly legacy?: boolean;
}

const MODELS_BY_HOST_SUFFIX: Readonly<Record<string, readonly StaticModel[]>> =
  {
    // Production (dimeterminal.ai) and legacy (terminal.dime.gg) gateways.
    'dimeterminal.ai': [
      { id: 'dtmodel/bedrock/anthropic/opus/5' },
      { id: 'dtmodel/bedrock/anthropic/fable/5' },
      { id: 'dtmodel/bedrock/anthropic/opus/4.8' },
      { id: 'dtmodel/bedrock/anthropic/opus/4.7', legacy: true },
      { id: 'dtmodel/bedrock/anthropic/sonnet/5' },
      { id: 'dtmodel/bedrock/anthropic/sonnet/4.6' },
      { id: 'dtmodel/bedrock/anthropic/sonnet/4.5' },
      { id: 'dtmodel/bedrock/anthropic/haiku/4.5' },
      { id: 'dtmodel/bedrock/deepseek/deepseek-v3.2' },
      { id: 'dtmodel/bedrock/deepseek/deepseek-v3' },
      { id: 'dtmodel/bedrock/google/gemma-3/27b' },
      { id: 'dtmodel/bedrock/qwen/qwen-3/235b' },
      { id: 'dtmodel/bedrock/qwen/qwen-3/coder-480b' },
      { id: 'dtmodel/bedrock/openai/gpt-oss/120b' },
      { id: 'dtmodel/bedrock/mistral/mistral-large-3/675b' },
      { id: 'dtmodel/bedrock/z-ai/glm-5' },
      { id: 'dtmodel/bedrock/z-ai/glm-4.7' },
      { id: 'dtmodel/bedrock/moonshot-ai/kimi-k2.5' },
      { id: 'dtmodel/qwen/qwen/qwen-3.7/max' },
      { id: 'dtmodel/moonshot-ai/moonshot-ai/kimi-k3' },
      { id: 'dtmodel/fireworks-ai/moonshot-ai/kimi-k3' },
      { id: 'dtmodel/z-ai/z-ai/glm-5.2' },
      { id: 'dtmodel/fireworks-ai/z-ai/glm-5.2' },
    ],
    'terminal.dime.gg': [
      { id: 'dtmodel/bedrock/anthropic/opus/5' },
      { id: 'dtmodel/bedrock/anthropic/fable/5' },
      { id: 'dtmodel/bedrock/anthropic/opus/4.8' },
      { id: 'dtmodel/bedrock/anthropic/opus/4.7', legacy: true },
      { id: 'dtmodel/bedrock/anthropic/sonnet/5' },
      { id: 'dtmodel/bedrock/anthropic/sonnet/4.6' },
      { id: 'dtmodel/bedrock/anthropic/sonnet/4.5' },
      { id: 'dtmodel/bedrock/anthropic/haiku/4.5' },
      { id: 'dtmodel/bedrock/deepseek/deepseek-v3.2' },
      { id: 'dtmodel/bedrock/google/gemma-3/27b' },
      { id: 'dtmodel/bedrock/qwen/qwen-3/235b' },
      { id: 'dtmodel/bedrock/openai/gpt-oss/120b' },
      { id: 'dtmodel/bedrock/mistral/mistral-large-3/675b' },
      { id: 'dtmodel/bedrock/z-ai/glm-5' },
    ],
  };

// Unknown gateways: a minimal Anthropic-only fallback that the live fetch
// corrects once it loads.
const DEFAULT_MODELS: readonly StaticModel[] = [
  { id: 'dtmodel/bedrock/anthropic/opus/4.8' },
  { id: 'dtmodel/bedrock/anthropic/sonnet/4.6' },
  { id: 'dtmodel/bedrock/anthropic/haiku/4.5' },
];

function toModelInfo(m: StaticModel): ModelInfo {
  const label = defaultModelLabel(m.id);
  return m.legacy === true
    ? { id: m.id, label, legacy: true }
    : { id: m.id, label };
}

/**
 * Offline fallback list for the gateway at `agentUrl`. Returns `[]` for an
 * unparseable/empty URL so the picker simply stays hidden until the live fetch
 * lands, rather than guessing a wrong list.
 */
export function getStaticModels(agentUrl: string | null): readonly ModelInfo[] {
  if (agentUrl == null || agentUrl.length === 0) return [];
  let host: string;
  try {
    host = new URL(agentUrl).hostname;
  } catch {
    return [];
  }
  for (const [suffix, models] of Object.entries(MODELS_BY_HOST_SUFFIX)) {
    if (host === suffix || host.endsWith(`.${suffix}`)) {
      return models.map(toModelInfo);
    }
  }
  return DEFAULT_MODELS.map(toModelInfo);
}
